import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
} from "lucide-react";
import React from "react";
import {
  cardReadiness,
  composerCardImageSrc,
  isStagedCard,
  type ComposerCard,
} from "src/store/happeningNowComposerStore";

interface ComposerCarouselProps {
  cards: ComposerCard[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onRemove: (key: string) => void;
  /** Reorder by drag: move `activeKey` to where `overKey` sits. */
  onReorder: (activeKey: string, overKey: string) => void;
  /** Add cards from images picked via the trailing "+" tile. */
  onAdd: (files: File[]) => void;
}

const chevronClass =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[#D0D0D0] bg-white text-[#555555] shadow-[0_2px_0_#D0D0D0] transition-colors hover:border-[#14B8A6] hover:text-[#0F766E] disabled:cursor-not-allowed disabled:opacity-40";

interface SortableThumbProps {
  card: ComposerCard;
  index: number;
  isSelected: boolean;
  onSelect: (index: number) => void;
}

/** A draggable thumbnail. A short drag reorders; a plain click selects. */
const SortableThumb: React.FC<SortableThumbProps> = ({
  card,
  index,
  isSelected,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.key });
  const ready = cardReadiness(card).ready;

  return (
    <button
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 20 : undefined,
      }}
      {...attributes}
      {...listeners}
      type="button"
      role="tab"
      aria-selected={isSelected}
      aria-label={`Card ${index + 1}`}
      onClick={() => onSelect(index)}
      className={[
        "relative aspect-video w-16 shrink-0 touch-none overflow-hidden rounded-lg border-2 transition-all",
        isDragging ? "cursor-grabbing" : "cursor-grab",
        isSelected
          ? "-translate-y-0.5 border-[#14B8A6] shadow-[0_0_0_2px_#14B8A6,0_4px_10px_rgba(0,0,0,0.18)]"
          : "border-[#C4C4C4] opacity-70 ring-1 ring-black/5 hover:-translate-y-0.5 hover:border-[#14B8A6]/60 hover:opacity-100",
      ].join(" ")}
    >
      <img
        src={composerCardImageSrc(card)}
        alt=""
        className="pointer-events-none h-full w-full object-cover"
      />
      <span
        className={[
          "absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full ring-1 ring-white",
          ready ? "bg-[#14B8A6]" : "bg-[#F59E0B]",
        ].join(" ")}
        aria-hidden
      />
    </button>
  );
};

/**
 * Slidable strip of staged cards: a large focus card with prev/next chevrons,
 * a per-card status badge, and a thumbnail rail for jumping directly to any
 * card. Scales to many cards — the rail scrolls.
 */
export const ComposerCarousel: React.FC<ComposerCarouselProps> = ({
  cards,
  selectedIndex,
  onSelect,
  onRemove,
  onReorder,
  onAdd,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const selected = cards[selectedIndex];
  if (!selected) return null;

  const readiness = cardReadiness(selected);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  return (
    <section
      className="rounded-[1.75rem] border-4 border-white/10 bg-white/[0.04] p-4 shadow-[0_6px_0_rgba(0,0,0,0.3)] backdrop-blur-sm sm:p-5"
      aria-label="Staged cards"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Previous card"
          disabled={selectedIndex === 0}
          onClick={() => onSelect(selectedIndex - 1)}
          className={chevronClass}
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>

        <div className="relative mx-auto aspect-video w-full max-w-xl overflow-hidden rounded-2xl bg-[#F7F7F7] ring-1 ring-black/10 shadow-[0_4px_16px_rgba(0,0,0,0.14)]">
          <img
            src={composerCardImageSrc(selected)}
            alt=""
            className="h-full w-full object-cover"
          />
          <span className="absolute left-2 top-2 flex flex-col items-start gap-1">
            <span className="rounded-full bg-black/70 px-2.5 py-1 text-xs font-extrabold text-white">
              Card {selectedIndex + 1} of {cards.length}
            </span>
            {isStagedCard(selected) ? (
              <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#4338CA] ring-1 ring-[#6366F1]/40">
                new
              </span>
            ) : (
              <span className="rounded-full bg-[#ECFDF5] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#0F766E] ring-1 ring-[#14B8A6]/40">
                in draft
              </span>
            )}
          </span>
          {readiness.ready ? (
            <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2.5 py-1 text-xs font-extrabold text-[#0F766E] ring-1 ring-[#14B8A6]/40">
              <Check className="h-3 w-3" aria-hidden /> ready
            </span>
          ) : (
            <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2.5 py-1 text-xs font-extrabold text-[#92400E] ring-1 ring-[#F59E0B]/40">
              <AlertTriangle className="h-3 w-3" aria-hidden />{" "}
              {readiness.reason}
            </span>
          )}
          <button
            type="button"
            aria-label="Remove this card"
            onClick={() => onRemove(selected.key)}
            className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-[#EF4444]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <button
          type="button"
          aria-label="Next card"
          disabled={selectedIndex === cards.length - 1}
          onClick={() => onSelect(selectedIndex + 1)}
          className={chevronClass}
        >
          <ChevronRight className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <p className="mt-3 text-center text-[11px] font-bold text-white/50">
        Drag thumbnails to reorder · tap to jump
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={cards.map((c) => c.key)}
          strategy={horizontalListSortingStrategy}
        >
          <div
            className="mt-1 flex gap-2 overflow-x-auto px-0.5 pb-2 pt-2 [scrollbar-width:thin]"
            role="tablist"
            aria-label="Jump to card"
          >
            {cards.map((card, index) => (
              <SortableThumb
                key={card.key}
                card={card}
                index={index}
                isSelected={index === selectedIndex}
                onSelect={onSelect}
              />
            ))}
            <label
              aria-label="Add card"
              title="Add card"
              className="flex aspect-video w-16 shrink-0 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-white/25 text-white/50 transition-colors hover:border-[#14B8A6] hover:text-[#14B8A6]"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                onChange={(e) => {
                  if (e.target.files) onAdd(Array.from(e.target.files));
                  e.target.value = "";
                }}
              />
              <Plus className="h-5 w-5" aria-hidden />
            </label>
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
};
